// Inquiry detail: header (restaurant + credit + assignee + date), item list with
// notes, and expandable per-category sub-inquiries. Header + items come from the
// API (GET api/v1/cms/restaurant-inquiries/:id). Supplier quotes have no backend
// yet, so the fetched inquiry is mirrored into the localStorage store (preserving
// any locally saved quotes) and the quote flows keep operating on that mirror.

import {
  InquiryQuoteSource,
  RestaurantInquiry,
  RestaurantInquiryItem,
  RestaurantInquiryItemStatus,
  RestaurantInquiryQuote,
  RestaurantInquiryStatus,
  useDeleteRestaurantInquiryItemApi,
  useGetRestaurantInquiryApi,
  useUpdateRestaurantInquiryItemStatusApi,
  useUpdateRestaurantInquiryStatusApi,
} from 'apis/restaurant-inquiries';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Card,
  CardBody,
  Center,
  Flex,
  Heading,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from 'design-system';
import { AddItemQuoteModal } from 'pages/inquiry-processor/components/add-item-quote-modal';
import { AddItemsModal } from 'pages/inquiry-processor/components/add-items-modal';
import { EditItemModal } from 'pages/inquiry-processor/components/edit-item-modal';
import { InquiryActivity } from 'pages/inquiry-processor/components/inquiry-activity';
import { QuoteDecisionModal } from 'pages/inquiry-processor/components/quote-decision-modal';
import { QuoteWorkbenchDrawer } from 'pages/inquiry-processor/components/quote-workbench-drawer';
import { SendOrderModal } from 'pages/inquiry-processor/components/send-order-modal';
import { StatusReasonModal } from 'pages/inquiry-processor/components/status-reason-modal';
import { useInquiryStore } from 'pages/inquiry-processor/hooks/use-inquiry-store';
import type { Inquiry, InquiryStatus, Item } from 'pages/inquiry-processor/types';
import { isGuestInquiry } from 'pages/inquiry-processor/utils/app-request';
import { apiErrorMessage } from 'pages/inquiry-processor/utils/inquiry-actions';
import { paymentLabel } from 'pages/inquiry-processor/utils/payment';
import { quoteProductLabel } from 'pages/inquiry-processor/utils/quotable';
import { fmt, groupByCat, qtyStr } from 'pages/inquiry-processor/utils/scoring';
import {
  inquiryStatusMeta,
  inquiryStatusRequiresReason,
  itemStatusMeta,
  nextItemStatuses,
  NEXT_INQUIRY_STATUSES,
} from 'pages/inquiry-processor/utils/status';
import { detectCat, InquiryStore } from 'pages/inquiry-processor/utils/storage';
import { useEffect, useMemo, useState } from 'react';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';

// Backend → local status, for the localStorage mirror the quote flows read.
const LOCAL_STATUS: Record<RestaurantInquiryStatus, InquiryStatus> = {
  [RestaurantInquiryStatus.DRAFT]: 'new',
  [RestaurantInquiryStatus.NEW]: 'new',
  [RestaurantInquiryStatus.PROCESSING]: 'processing',
  [RestaurantInquiryStatus.QUOTATION_READY]: 'quote_ready',
  [RestaurantInquiryStatus.PARTIALLY_FULFILLED]: 'processing',
  [RestaurantInquiryStatus.FULFILLED]: 'closed_won',
  [RestaurantInquiryStatus.UNFULFILLED]: 'closed_lost',
  [RestaurantInquiryStatus.CANCELLED]: 'closed_lost',
};

const companyName = (inq: RestaurantInquiry) => inq.restaurant?.nameEn || `Restaurant #${inq.restaurantId}`;
const creditLabel = (inq: RestaurantInquiry) => paymentLabel(inq.paymentOption, inq.creditDays);

// Grouping category for an item: the real catalog L2 (depth 1 in the item's
// categoryTree) when the API provides it, falling back to the name heuristic.
const itemCat = (it: RestaurantInquiryItem): string => {
  const tree = it.masterProduct?.categoryTree;
  if (tree?.length) {
    const l2 = tree.find((n) => n.depth === 1);
    return (l2 || tree[0]).name; // tree is leaf-first; [0] is the most specific
  }
  return detectCat(it.masterProduct?.name || '');
};

// Map API items onto the local Item shape the scoring/quote helpers understand.
const toLocalItems = (inq: RestaurantInquiry): Item[] =>
  (inq.items || []).map((it) => ({
    id: String(it.id),
    name: it.masterProduct?.name || `Product #${it.masterProductId}`,
    category: itemCat(it),
    brand: it.masterProduct?.brand || undefined,
    brandOS: it.brandFlexible,
    originOS: it.originFlexible,
    uom: it.masterProduct?.baseUnit || undefined,
    quantity: it.volume ? String(it.volume) : undefined,
    currentPrice: it.currentPrice != null ? String(it.currentPrice) : undefined,
    targetPrice: it.expectedPrice != null ? String(it.expectedPrice) : undefined,
    notes: it.notes || undefined,
  }));

export const InquiryDetailScreen = () => {
  useInquiryStore();
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const addItems = useDisclosure();
  const addQuote = useDisclosure();
  const sendOrderModal = useDisclosure();

  // Which category the Add Quote modal is acting on.
  const [target, setTarget] = useState<{ category: string }>({ category: '' });
  // Raw API item being edited (null = edit modal closed).
  const [editTarget, setEditTarget] = useState<RestaurantInquiryItem | null>(null);
  // Raw API item receiving a new supplier quote (null = quote modal closed).
  const [quoteTarget, setQuoteTarget] = useState<RestaurantInquiryItem | null>(null);
  // Quote being accepted/rejected (null = decision modal closed).
  const [decision, setDecision] = useState<{
    itemId: number;
    quote: RestaurantInquiryQuote;
    mode: 'accept' | 'reject';
  } | null>(null);

  const toast = useToast();
  const { data: apiInq, isLoading, isError, error, refetch } = useGetRestaurantInquiryApi(id);
  const updateStatus = useUpdateRestaurantInquiryStatusApi();
  const updateItemStatus = useUpdateRestaurantInquiryItemStatusApi();

  // Target status awaiting its mandatory reason (null = reason modal closed).
  const [reasonTarget, setReasonTarget] = useState<RestaurantInquiryStatus | null>(null);

  const doChangeStatus = async (status: RestaurantInquiryStatus, extra?: { reason?: string }) => {
    try {
      await updateStatus.mutateAsync({ id, status, ...extra });
      toast({ title: `Status changed to ${inquiryStatusMeta(status).label}`, status: 'success' });
      return true;
    } catch (err) {
      toast({ title: 'Failed to change status', description: apiErrorMessage(err), status: 'error' });
      return false;
    }
  };

  const changeStatus = (status: RestaurantInquiryStatus) => {
    // FULFILLED needs nothing extra — orders are created via Send Order, so the
    // old order-id prompt is gone.
    if (inquiryStatusRequiresReason(status)) {
      setReasonTarget(status);
      return;
    }
    doChangeStatus(status);
  };

  const confirmStatusReason = async (reason: string) => {
    if (!reasonTarget) return;
    const ok = await doChangeStatus(reasonTarget, { reason });
    if (ok) setReasonTarget(null);
  };

  const changeItemStatus = async (itemId: number, status: RestaurantInquiryItemStatus) => {
    try {
      await updateItemStatus.mutateAsync({ id, itemId, status });
      toast({ title: `Item marked ${itemStatusMeta(status).label}`, status: 'success' });
    } catch (err) {
      toast({ title: 'Failed to change item status', description: apiErrorMessage(err), status: 'error' });
    }
  };

  const deleteItem = useDeleteRestaurantInquiryItemApi();

  // Soft delete server-side; no restore endpoint, so confirm first.
  const removeItem = async (item: RestaurantInquiryItem) => {
    const name = item.masterProduct?.name || `item #${item.id}`;
    if (!window.confirm(`Remove “${name}” from this inquiry? This cannot be undone.`)) return;
    try {
      await deleteItem.mutateAsync({ id, itemId: item.id });
      toast({ title: 'Item removed', status: 'success' });
    } catch (err) {
      toast({ title: 'Failed to remove item', description: apiErrorMessage(err), status: 'error' });
    }
  };

  useEffect(() => {
    dispatch(changeTitle('GPO — Inquiry Processor'));
  }, [dispatch]);

  const items = useMemo(() => (apiInq ? toLocalItems(apiInq) : []), [apiInq]);

  // Mirror the fetched inquiry into the local store (keyed by the backend id)
  // so the quote modals and the comparison screen — still localStorage-backed —
  // operate on current data. Locally saved supplier quotes are preserved.
  useEffect(() => {
    if (!apiInq) return;
    const existing = InquiryStore.get(id);
    const mirror: Inquiry = {
      id,
      backendId: apiInq.id,
      company: companyName(apiInq),
      restaurantId: apiInq.restaurantId,
      credit: creditLabel(apiInq),
      paymentOption: apiInq.paymentOption,
      creditDays: apiInq.creditDays,
      additionalReq: apiInq.notes || '',
      status: LOCAL_STATUS[apiInq.status] || 'new',
      items,
      subInquiries: existing?.subInquiries || [],
      createdAt: apiInq.createdAt ? new Date(apiInq.createdAt).getTime() : Date.now(),
    };
    InquiryStore.upsert(mirror);
  }, [apiInq, id, items]);

  // Deep-link support for the Unmatched Demand scan popup's "View Inquiry"
  // action — jump to the referenced item once the list has rendered.
  useEffect(() => {
    if (!apiInq) return;
    const itemId = new URLSearchParams(location.search).get('itemId');
    if (!itemId) return;
    document.getElementById(`inquiry-item-${itemId}`)?.scrollIntoView({ block: 'nearest' });
  }, [apiInq, location.search]);

  // Send Order is available while at least one awarded quote has not been sent yet.
  const hasSendableQuotes = useMemo(
    () => (apiInq?.items ?? []).some((item) => (item.quotes ?? []).some((q) => q.status === 'ACCEPTED' && !q.orderId)),
    [apiInq]
  );

  if (isLoading) {
    return (
      <Center py='24'>
        <Spinner color='red.500' />
      </Center>
    );
  }

  if (isError || !apiInq) {
    const notFound = (error as { response?: { status?: number } })?.response?.status === 404;
    return (
      <Box p='6'>
        <Heading size='md'>{notFound ? 'Inquiry not found' : 'Couldn’t load inquiry'}</Heading>
        <Flex gap='2' mt='4'>
          {!notFound && (
            <Button colorScheme='red' onClick={() => refetch()}>
              Retry
            </Button>
          )}
          <Button variant='outline' onClick={() => history.push('/app/inquiry-processor/list')}>
            ← All Inquiries
          </Button>
        </Flex>
      </Box>
    );
  }

  const company = companyName(apiInq);
  const apiItems = apiInq.items ?? [];
  const meta = inquiryStatusMeta(apiInq.status);
  const nextStatuses = NEXT_INQUIRY_STATUSES[apiInq.status] || [];
  const grouped = groupByCat(items);

  const openAddQuote = (category: string) => {
    setTarget({ category });
    addQuote.onOpen();
  };

  return (
    <Box p={{ base: '3', md: '6' }}>
      <Breadcrumb mb='4' fontSize='sm' color='gray.500'>
        <BreadcrumbItem>
          <BreadcrumbLink onClick={() => history.push('/app/inquiry-processor/list')}>Inquiries</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <Text>{company}</Text>
        </BreadcrumbItem>
      </Breadcrumb>

      <Flex justify='space-between' align='center' mb='4' wrap='wrap' gap='2'>
        <Flex align='center' gap='2' wrap='wrap'>
          <Heading size='lg'>{company}</Heading>
          {nextStatuses.length ? (
            <Menu>
              <MenuButton
                as={Button}
                size='sm'
                variant='outline'
                colorScheme={meta.colorScheme}
                isLoading={updateStatus.isLoading}>
                {meta.label} ▾
              </MenuButton>
              <MenuList>
                {nextStatuses.map((s) => (
                  <MenuItem key={s} onClick={() => changeStatus(s)}>
                    {inquiryStatusMeta(s).label}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          ) : (
            <Badge colorScheme={meta.colorScheme}>{meta.label}</Badge>
          )}
          <Badge colorScheme='cyan'>Credit: {creditLabel(apiInq)}</Badge>
          <Badge colorScheme='blue'>{fmt(apiInq.createdAt ? new Date(apiInq.createdAt).getTime() : undefined)}</Badge>
          {apiInq.creator && (
            <Badge colorScheme='gray'>Created by: {apiInq.creator.name || apiInq.creator.email}</Badge>
          )}
          {isGuestInquiry(apiInq) && <Badge colorScheme='red'>Guest</Badge>}
          {apiInq.assignee && (
            <Badge colorScheme='purple'>Assigned: {apiInq.assignee.name || apiInq.assignee.email}</Badge>
          )}
        </Flex>
        <Flex gap='2'>
          {hasSendableQuotes && (
            <Button size='sm' colorScheme='red' onClick={sendOrderModal.onOpen}>
              Send Order
            </Button>
          )}
          <Button size='sm' variant='outline' onClick={addItems.onOpen}>
            Manage Items
          </Button>
        </Flex>
      </Flex>

      <Card variant='outline' mb='4'>
        <CardBody>
          <Text
            fontSize='sm'
            fontWeight='semibold'
            color='gray.500'
            textTransform='uppercase'
            letterSpacing='wide'
            mb='2'>
            Item List{' '}
            <Text as='span' fontWeight='normal' textTransform='none'>
              ({items.length} item{items.length !== 1 ? 's' : ''})
            </Text>
          </Text>
          <Box maxH='170px' overflowY='auto'>
            {items.map((it) => {
              const raw = apiItems.find((a) => String(a.id) === it.id);
              const iMeta = itemStatusMeta(raw?.status);
              const iNext = nextItemStatuses(raw?.status);
              const quotes = raw?.quotes || [];
              const specs = [
                it.brand ? `Brand: ${it.brand}${raw?.brandFlexible ? ' (OS)' : ''}` : null,
                `Origin: ${raw?.originFlexible ? 'open to switch' : 'fixed'}`,
                it.uom ? `UOM: ${it.uom}` : null,
                it.category || null,
                raw?.currentPrice != null ? `Curr ${raw.currentPrice} ${raw.currency}` : null,
                raw?.expectedPrice != null ? `Target ${raw.expectedPrice} ${raw.currency}` : null,
                raw?.notes || null,
                raw?.creator?.name
                  ? `Added by ${raw.creator.name} · ${fmt(
                      raw.createdAt ? new Date(raw.createdAt).getTime() : undefined
                    )}`
                  : null,
              ]
                .filter(Boolean)
                .join('  |  ');
              const isLinkedItem = new URLSearchParams(location.search).get('itemId') === it.id;
              return (
                <Box
                  key={it.id}
                  id={`inquiry-item-${it.id}`}
                  py='1.5'
                  borderBottom='1px solid'
                  borderColor='gray.100'
                  fontSize='sm'
                  bg={isLinkedItem ? 'yellow.50' : undefined}>
                  <Flex justify='space-between' align='center'>
                    <Box minW='0'>
                      <Text fontWeight='medium'>{it.name}</Text>
                      <Text fontSize='xs' color='gray.500' noOfLines={1}>
                        {specs}
                      </Text>
                    </Box>
                    <Flex align='center' gap='2'>
                      {raw &&
                        (iNext.length ? (
                          <Menu>
                            <MenuButton
                              as={Button}
                              size='xs'
                              variant='outline'
                              colorScheme={iMeta.colorScheme}
                              isLoading={updateItemStatus.isLoading}>
                              {iMeta.label} ▾
                            </MenuButton>
                            <MenuList>
                              {iNext.map((s) => (
                                <MenuItem key={s} onClick={() => changeItemStatus(raw.id, s)}>
                                  {itemStatusMeta(s).label}
                                </MenuItem>
                              ))}
                            </MenuList>
                          </Menu>
                        ) : (
                          <Badge colorScheme={iMeta.colorScheme}>{iMeta.label}</Badge>
                        ))}
                      <Text color='gray.500'>{qtyStr(it)}</Text>
                      <Button size='xs' variant='outline' onClick={() => raw && setQuoteTarget(raw)}>
                        + Quote
                      </Button>
                      <IconButton
                        aria-label='Edit item'
                        size='xs'
                        variant='ghost'
                        icon={<FiEdit2 />}
                        onClick={() => raw && setEditTarget(raw)}
                      />
                      <IconButton
                        aria-label='Remove item'
                        size='xs'
                        variant='ghost'
                        icon={<FiTrash2 />}
                        isDisabled={deleteItem.isLoading}
                        onClick={() => raw && removeItem(raw)}
                      />
                    </Flex>
                  </Flex>
                  {quotes.length > 0 && (
                    <Box pl='4' pt='1'>
                      {quotes.map((q) => (
                        <Flex key={q.id} align='center' gap='2' py='0.5' fontSize='xs' color='gray.600' wrap='wrap'>
                          <Badge
                            colorScheme={q.status === 'ACCEPTED' ? 'green' : q.status === 'REJECTED' ? 'red' : 'orange'}
                            variant='subtle'>
                            {q.status}
                          </Badge>
                          <Text>
                            {q.supplier?.name || `Supplier #${q.supplierId}`} — {quoteProductLabel(q)}
                          </Text>
                          <Text fontWeight='semibold'>
                            {q.price} {q.currency}
                          </Text>
                          {q.metadata?.lostToQuoteId && <Text color='gray.400'>(lost to competing offer)</Text>}
                          {q.orderId && (
                            <Text as='span'>
                              ·{' '}
                              {q.order?.code ? (
                                <Link to={`/app/orders/details/${q.order.code}`}>
                                  <Text as='span' color='red.500' fontWeight='500' _hover={{ color: 'red.600' }}>
                                    Order {q.order.code}
                                  </Text>
                                </Link>
                              ) : (
                                <Text as='span'>Order #{q.orderId}</Text>
                              )}
                              {q.orderedByUser?.name && ` · Sent by ${q.orderedByUser.name}`}
                            </Text>
                          )}
                          {q.notes && (
                            <Text color='gray.400' noOfLines={1}>
                              · {q.notes}
                            </Text>
                          )}
                          {/* Auto-quotes have no creator — attribute them to "Auto". */}
                          {(q.creator?.name || q.source === InquiryQuoteSource.AUTO) && (
                            <Text color='gray.400'>
                              · by {q.source === InquiryQuoteSource.AUTO ? 'Auto' : q.creator?.name} ·{' '}
                              {fmt(q.createdAt ? new Date(q.createdAt).getTime() : undefined)}
                            </Text>
                          )}
                          {q.status === 'PENDING' && raw && (
                            <>
                              <Button
                                size='xs'
                                variant='outline'
                                colorScheme='green'
                                onClick={() => setDecision({ itemId: raw.id, quote: q, mode: 'accept' })}>
                                Accept
                              </Button>
                              <Button
                                size='xs'
                                variant='outline'
                                colorScheme='red'
                                onClick={() => setDecision({ itemId: raw.id, quote: q, mode: 'reject' })}>
                                Reject
                              </Button>
                            </>
                          )}
                        </Flex>
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
          <Box borderTop='1px solid' borderColor='gray.200' mt='3' pt='3'>
            <Text
              fontSize='sm'
              fontWeight='semibold'
              color='gray.500'
              textTransform='uppercase'
              letterSpacing='wide'
              mb='1'>
              Notes
            </Text>
            <Text fontSize='sm'>{apiInq.notes || '—'}</Text>
          </Box>
        </CardBody>
      </Card>

      <Heading size='sm' mb='3'>
        Sub-Inquiries by Category
      </Heading>

      {Object.keys(grouped).length === 0 ? (
        <Card variant='outline'>
          <CardBody textAlign='center' color='gray.400'>
            <Text>No items on this inquiry.</Text>
          </CardBody>
        </Card>
      ) : (
        <Accordion allowMultiple>
          {Object.entries(grouped).map(([cat, catItems]) => {
            // Supplier quotes come ONLY from the API (items[].quotes[] on
            // GET :id) — grouped by supplier across this category's items.
            const catRaw = catItems
              .map((ci) => apiItems.find((a) => String(a.id) === ci.id))
              .filter((a): a is RestaurantInquiryItem => !!a);
            const bySupplier = new Map<number, { name: string; quotes: RestaurantInquiryQuote[] }>();
            catRaw.forEach((ri) =>
              (ri.quotes || []).forEach((q) => {
                const rec = bySupplier.get(q.supplierId) || {
                  name: q.supplier?.name || `Supplier #${q.supplierId}`,
                  quotes: [],
                };
                rec.quotes.push(q);
                bySupplier.set(q.supplierId, rec);
              })
            );
            const sups = Array.from(bySupplier.values());
            const supEntries = Array.from(bySupplier.entries());
            return (
              <AccordionItem key={cat} bg='white' border='1px solid' borderColor='gray.200' rounded='md' mb='3'>
                <AccordionButton _expanded={{ bg: 'gray.50' }} py='3'>
                  <AccordionIcon mr='2' />
                  <Flex flex='1' align='center' gap='3' textAlign='left' wrap='wrap'>
                    <Badge colorScheme='purple'>{cat}</Badge>
                    <Text fontWeight='semibold'>
                      {catItems.length} item{catItems.length !== 1 ? 's' : ''}
                    </Text>
                    <Text fontSize='sm' color='gray.500'>
                      {sups.length} supplier quote{sups.length !== 1 ? 's' : ''}
                    </Text>
                    <Text fontSize='sm' color='gray.400' ml='auto' noOfLines={1}>
                      {catItems
                        .slice(0, 3)
                        .map((i) => (i.brand ? `${i.name} (${i.brand})` : i.name))
                        .join(', ')}
                      {catItems.length > 3 ? ' …' : ''}
                    </Text>
                  </Flex>
                </AccordionButton>
                <AccordionPanel pb='4'>
                  {/* Requested items in this category, with their specs. */}
                  <Box mb='3' bg='gray.50' rounded='md' px='3' py='2'>
                    {catItems.map((ci) => (
                      <Flex key={ci.id} align='baseline' gap='2' py='0.5' fontSize='xs' wrap='wrap'>
                        <Text fontWeight='semibold' fontSize='sm'>
                          {ci.name}
                        </Text>
                        <Text color='gray.500'>
                          {[
                            ci.brand ? `Brand: ${ci.brand}${ci.brandOS ? ' (OS)' : ''}` : null,
                            `Origin: ${ci.originOS ? 'open to switch' : 'fixed'}`,
                            ci.uom ? `UOM: ${ci.uom}` : null,
                            qtyStr(ci) || null,
                            ci.currentPrice ? `Curr ${ci.currentPrice} AED` : null,
                            ci.targetPrice ? `Target ${ci.targetPrice} AED` : null,
                            ci.notes || null,
                          ]
                            .filter(Boolean)
                            .join('  |  ')}
                        </Text>
                      </Flex>
                    ))}
                  </Box>
                  {/* Quote comparison — items as rows, suppliers as columns,
                      built exclusively from items[].quotes[] (API). The lowest
                      undecided/accepted price per row is highlighted. */}
                  {supEntries.length > 0 && (
                    <Box overflowX='auto' mb='2'>
                      <Table size='sm' variant='simple'>
                        <Thead>
                          <Tr>
                            <Th>Item</Th>
                            {supEntries.map(([sid, s]) => (
                              <Th key={sid}>{s.name}</Th>
                            ))}
                          </Tr>
                        </Thead>
                        <Tbody>
                          {catRaw.map((ri) => {
                            const rowQuotes = (ri.quotes || []).filter((q) => q.status !== 'REJECTED');
                            const minPrice = rowQuotes.length ? Math.min(...rowQuotes.map((q) => q.price)) : null;
                            return (
                              <Tr key={ri.id}>
                                <Td fontWeight='medium' whiteSpace='nowrap'>
                                  {ri.masterProduct?.name || `Item #${ri.id}`}
                                </Td>
                                {supEntries.map(([sid]) => {
                                  // A supplier may quote the same item with several
                                  // SKUs (uniqueness is per item+product) — show all.
                                  const cellQuotes = (ri.quotes || []).filter((x) => x.supplierId === sid);
                                  if (!cellQuotes.length)
                                    return (
                                      <Td key={sid} color='gray.300'>
                                        —
                                      </Td>
                                    );
                                  const hasBest = cellQuotes.some(
                                    (q) => q.status !== 'REJECTED' && minPrice != null && q.price === minPrice
                                  );
                                  return (
                                    <Td key={sid} bg={hasBest ? 'green.50' : undefined} verticalAlign='top'>
                                      {cellQuotes.map((q) => (
                                        <Box key={q.id} mb='1'>
                                          <Text fontWeight='semibold' whiteSpace='nowrap'>
                                            {q.price} {q.currency}
                                            {cellQuotes.length > 1 && (
                                              <Text as='span' fontWeight='normal' color='gray.500' fontSize='xs'>
                                                {' '}
                                                · {quoteProductLabel(q)}
                                              </Text>
                                            )}
                                          </Text>
                                          <Badge
                                            colorScheme={
                                              q.status === 'ACCEPTED'
                                                ? 'green'
                                                : q.status === 'REJECTED'
                                                ? 'red'
                                                : 'orange'
                                            }
                                            variant='subtle'>
                                            {q.status}
                                          </Badge>
                                          {q.orderId && q.order?.code && (
                                            <Link to={`/app/orders/details/${q.order.code}`}>
                                              <Text
                                                fontSize='xs'
                                                color='red.500'
                                                fontWeight='500'
                                                _hover={{ color: 'red.600' }}>
                                                Order {q.order.code}
                                                {q.orderedByUser?.name ? ` · ${q.orderedByUser.name}` : ''}
                                              </Text>
                                            </Link>
                                          )}
                                          {q.status === 'PENDING' && (
                                            <Flex gap='1' mt='1'>
                                              <Button
                                                size='xs'
                                                variant='outline'
                                                colorScheme='green'
                                                onClick={() =>
                                                  setDecision({ itemId: ri.id, quote: q, mode: 'accept' })
                                                }>
                                                Accept
                                              </Button>
                                              <Button
                                                size='xs'
                                                variant='outline'
                                                colorScheme='red'
                                                onClick={() =>
                                                  setDecision({ itemId: ri.id, quote: q, mode: 'reject' })
                                                }>
                                                Reject
                                              </Button>
                                            </Flex>
                                          )}
                                        </Box>
                                      ))}
                                    </Td>
                                  );
                                })}
                              </Tr>
                            );
                          })}
                        </Tbody>
                      </Table>
                    </Box>
                  )}
                  <Flex gap='2' mt='3' wrap='wrap'>
                    <Button size='sm' colorScheme='red' onClick={() => openAddQuote(cat)}>
                      + Add Quote
                    </Button>
                  </Flex>
                </AccordionPanel>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}

      <InquiryActivity inquiryId={id} items={apiItems} />

      {/* Mandatory reason before PARTIALLY_FULFILLED / UNFULFILLED / CANCELLED. */}
      <StatusReasonModal
        status={reasonTarget}
        onClose={() => setReasonTarget(null)}
        onConfirm={confirmStatusReason}
        isLoading={updateStatus.isLoading}
      />

      <AddItemsModal isOpen={addItems.isOpen} onClose={addItems.onClose} inquiryId={id} />
      {editTarget && (
        <EditItemModal isOpen={!!editTarget} onClose={() => setEditTarget(null)} inquiryId={id} item={editTarget} />
      )}
      {quoteTarget && (
        <AddItemQuoteModal
          isOpen={!!quoteTarget}
          onClose={() => setQuoteTarget(null)}
          inquiryId={id}
          item={quoteTarget}
        />
      )}
      {decision && (
        <QuoteDecisionModal
          isOpen={!!decision}
          onClose={() => setDecision(null)}
          inquiryId={id}
          itemId={decision.itemId}
          quote={decision.quote}
          mode={decision.mode}
        />
      )}
      {addQuote.isOpen && (
        <QuoteWorkbenchDrawer
          isOpen={addQuote.isOpen}
          onClose={addQuote.onClose}
          inquiryId={id}
          category={target.category}
          items={apiItems.filter((it) => itemCat(it) === target.category)}
        />
      )}
      {apiInq && <SendOrderModal isOpen={sendOrderModal.isOpen} onClose={sendOrderModal.onClose} inquiry={apiInq} />}
    </Box>
  );
};
